import React, { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { DragOverlay } from '@dnd-kit/core';
import { supabase } from '../lib/supabase';
import DroppableTimeSlot from './DroppableTimeSlot';
import type { Student, Vehicle, Schedule as ScheduleType, ScheduleViewSettings } from '../types';
import { DndContext, DragEndEvent, useSensor, useSensors, MouseSensor, TouchSensor, PointerSensor, closestCenter } from '@dnd-kit/core';

interface ScheduleProps {
  students: Student[];
  vehicles: Vehicle[];
  settings: ScheduleViewSettings;
  onSettingsChange: (settings: ScheduleViewSettings) => void;
  draggedStudent: Student | null;
  onDragEnd: () => void;
}

const Schedule: React.FC<ScheduleProps> = ({
  students,
  vehicles,
  settings,
  onSettingsChange,
  draggedStudent,
  onDragEnd
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState<ScheduleType[]>([]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor)
  );

  useEffect(() => {
    loadSchedules();
  }, [currentDate, settings.view]);

  const loadSchedules = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let startDate, endDate;
    
    if (settings.view === 'month') {
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
    } else if (settings.view === 'week') {
      startDate = startOfWeek(currentDate, { locale: ko });
      endDate = endOfWeek(currentDate, { locale: ko });
    } else {
      startDate = currentDate;
      endDate = currentDate;
    }

    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', format(startDate, 'yyyy-MM-dd'))
      .lte('date', format(endDate, 'yyyy-MM-dd'));

    if (error) {
      console.error('Error loading schedules:', error);
      return;
    }

    setSchedules(data || []);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || !active.data.current?.student) return;

    const student = active.data.current.student as Student;
    const [date, hour] = over.id.toString().split('-');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('schedules')
        .insert([{
          student_id: student.id,
          date,
          start_time: `${hour}:00`,
          end_time: `${parseInt(hour) + 1}:00`,
          user_id: user.id
        }]);

      if (error) throw error;
      
      loadSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('스케줄 저장 중 오류가 발생했습니다.');
    }

    onDragEnd();
  };

  const renderTimeSlots = () => {
    const slots = [];
    for (let hour = settings.startHour; hour < settings.endHour; hour++) {
      slots.push(
        <div key={hour} className="h-20 border-b border-gray-200 relative">
          <span className="absolute -top-3 left-0 text-sm text-gray-500">
            {hour}:00
          </span>
        </div>
      );
    }
    return slots;
  };

  const renderDayColumn = (date: Date) => {
    const daySlots = [];
    for (let hour = settings.startHour; hour < settings.endHour; hour++) {
      const slotId = `${format(date, 'yyyy-MM-dd')}-${hour}`;
      const slotSchedules = schedules.filter(schedule => 
        schedule.date === format(date, 'yyyy-MM-dd') &&
        parseInt(schedule.start_time.split(':')[0]) === hour
      );
      
      daySlots.push(
        <DroppableTimeSlot
          key={slotId}
          id={slotId}
          date={format(date, 'yyyy-MM-dd')}
          hour={hour}
          type="pickup"
          students={students}
          vehicles={vehicles}
          schedules={slotSchedules}
          onScheduleDelete={async (scheduleId) => {
            try {
              const { error } = await supabase
                .from('schedules')
                .delete()
                .eq('id', scheduleId);
              
              if (error) throw error;
              loadSchedules();
            } catch (error) {
              console.error('Error deleting schedule:', error);
              alert('스케줄 삭제 중 오류가 발생했습니다.');
            }
          }}
        />
      );
    }
    return daySlots;
  };

  const renderDays = () => {
    const days = [];
    let startDate, endDate;

    if (settings.view === 'month') {
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
    } else if (settings.view === 'week') {
      startDate = startOfWeek(currentDate, { locale: ko });
      endDate = endOfWeek(currentDate, { locale: ko });
    } else {
      startDate = currentDate;
      endDate = currentDate;
    }

    let currentDay = startDate;
    while (currentDay <= endDate) {
      if (settings.visibleDays.includes(currentDay.getDay())) {
        days.push(renderDayColumn(currentDay));
      }
      currentDay = addDays(currentDay, 1);
    }

    return days;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (settings.view === 'month') {
                  setCurrentDate(prev => addDays(prev, -30));
                } else {
                  setCurrentDate(prev => addDays(prev, settings.view === 'week' ? -7 : -1));
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeft size={20} />
            </button>
            
            <h2 className="text-xl font-semibold">
              {format(currentDate, settings.view === 'month' ? 'yyyy년 M월' : 'yyyy년 M월 d일', { locale: ko })}
            </h2>
            
            <button
              onClick={() => {
                if (settings.view === 'month') {
                  setCurrentDate(prev => addDays(prev, 30));
                } else {
                  setCurrentDate(prev => addDays(prev, settings.view === 'week' ? 7 : 1));
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={settings.view}
              onChange={(e) => onSettingsChange({ ...settings, view: e.target.value as 'month' | 'week' | 'day' })}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="month">월간</option>
              <option value="week">주간</option>
              <option value="day">일간</option>
            </select>
            
            <button
              onClick={() => setCurrentDate(new Date())}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <Calendar size={20} />
            </button>
          </div>
        </div>

        <div className="flex">
          <div className="w-16 pt-10">
            {renderTimeSlots()}
          </div>
          <div className="flex-1 flex">
            {renderDays()}
          </div>
        </div>

        <DragOverlay>
          {draggedStudent && (
            <div className="p-2 bg-blue-500 text-white rounded-md shadow-lg">
              {draggedStudent.name}
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default Schedule;